import { useFetch } from "./api/useFetch";
import { adaptStoreCoverage } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge } from "./ui-badges";
import { Loader2, AlertOctagon, MapPin } from "lucide-react";

export const StoreCoverage = ({ companyId }) => {
  const live = isSurfaceLive("STORE_COVERAGE");
  const { data, error, loading } = useFetch(
    live && companyId
      ? `/api/platform/diagnostics/store-coverage?company_id=${encodeURIComponent(companyId)}`
      : null
  );
  const cov = data ? adaptStoreCoverage(data) : null;

  return (
    <div
      data-testid={`store-coverage-${companyId || "none"}`}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Store coverage</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Department coverage per store (diagnostics).
          </p>
        </div>
        <LiveBadge live={live} />
      </div>

      {!live && (
        <p className="text-xs text-slate-500 text-center py-4">
          Store coverage is disabled by env flag.
        </p>
      )}

      {live && loading && (
        <div className="py-6 text-center">
          <Loader2 className="mx-auto h-5 w-5 text-slate-400 animate-spin" />
        </div>
      )}

      {live && error && (
        <div className="text-xs text-rose-700 flex items-start gap-2">
          <AlertOctagon className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error.kind === "forbidden" ? "Not authorized" : error.message}</span>
        </div>
      )}

      {live && cov && !error && (
        <div>
          <div className="flex items-center justify-between mb-3 text-xs">
            <span className="text-slate-500">Overall</span>
            <span className="font-semibold text-slate-900 tabular-nums">
              {cov.overallCoveragePct}%
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden mb-4">
            <div
              className="h-full bg-[#0d3b66]"
              style={{ width: `${Math.min(100, Math.max(0, cov.overallCoveragePct))}%` }}
            />
          </div>
          <ul className="space-y-2" data-testid="store-coverage-list">
            {cov.stores.length === 0 && (
              <li className="text-xs text-slate-500 text-center py-4">No store data returned.</li>
            )}
            {cov.stores.map((s) => (
              <li key={s.id || s.name} className="rounded-lg bg-slate-50 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-800 inline-flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-slate-400" />
                    {s.name}
                  </span>
                  <span className="text-xs tabular-nums text-slate-700">
                    {s.departmentsCovered}/{s.departmentsTotal}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500"
                    style={{ width: `${Math.min(100, Math.max(0, s.coveragePct))}%` }}
                  />
                </div>
                {s.missingDepartments.length > 0 && (
                  <div className="mt-1.5 text-[10px] text-amber-700">
                    Missing: {s.missingDepartments.slice(0, 5).join(", ")}
                    {s.missingDepartments.length > 5 && ` +${s.missingDepartments.length - 5} more`}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
